import React from 'react';
import { Check } from 'lucide-react';
import { Button } from '@business/workflows/ComponentUI';
import {
  SelectionBarAction,
  SelectionBarProps
} from '@business/interfaces/photo';
import { useTranslation } from 'react-i18next';

const isDangerAction = (action: SelectionBarAction) => {
  const iconClassName =
    React.isValidElement(action.icon) &&
    (action.icon.props as { className?: string })?.className;
  return typeof iconClassName === 'string' && iconClassName.includes('red');
};

export const SelectionBar: React.FC<SelectionBarProps> = ({
  selectedCount,
  itemType,
  actions,
  className = '',
  position = 'fixed',
  onDeselectAll
}) => {
  const { t } = useTranslation();
  const getItemLabel = () => {
    if (itemType === 'photo') {
      return selectedCount === 1 ? 'item' : 'items';
    } else if (itemType === 'folder') {
      return selectedCount === 1 ? 'folder' : 'folders';
    } else if (itemType === 'people') {
      return selectedCount === 1 ? 'person' : 'people';
    }
    return selectedCount === 1 ? 'item' : 'items';
  };

  if (selectedCount === 0) return null;

  const positionClasses =
    position === 'fixed'
      ? 'fixed top-16 left-0 md:left-64 right-0 z-40 mx-auto'
      : 'relative mb-3 rounded-sm';

  return (
    <div
      className={`bg-[#49712B] shadow-md px-6 py-3 flex items-center justify-between max-md:flex-col max-md:items-start max-md:gap-2 ${positionClasses} ${className}`}>
      <div className="flex items-center gap-3 pt-3">
        <button
          type="button"
          onClick={() => onDeselectAll?.()}
          aria-label={t('deselectAll')}
          title={t('deselectAll')}
          className="flex items-center justify-center w-5 h-5 rounded-sm bg-white shrink-0">
          <Check className="w-3.5 h-3.5 text-[#49712B]" strokeWidth={3} />
        </button>
        <span className="text-sm text-white">
          {selectedCount} {getItemLabel()} {t('selected')}
        </span>
      </div>
      <div className="flex items-center gap-2 pt-3 max-md:flex-wrap max-md:w-full">
        {actions.map((action, index) => {
          const isDanger = isDangerAction(action);
          return (
            <Button
              key={index}
              btnStyle="no-outline"
              onClick={action.onClick}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors [&_svg]:brightness-0 [&_svg]:invert ${
                isDanger
                  ? 'bg-red-600 border-red-600 text-white hover:bg-red-700'
                  : 'bg-[#49712B] border-white text-white hover:bg-[#3a5a21]'
              } ${action.className || ''}`}
              title={action.title}>
              {action.icon}
              {action.title}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
